import { useParams, Navigate, Link } from "react-router-dom";
import { getCategoryBySlug } from "@/data/categories";
import { blogPosts } from "@/data/blogPosts";
import { capsulas } from "@/data/capsulas";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import BlogCard from "@/components/BlogCard";
import CapsuleCard from "@/components/CapsuleCard";
import Footer from "@/components/Footer";

const Category = () => {
  const { categorySlug } = useParams();
  const category = getCategoryBySlug(categorySlug || "");

  if (!category) {
    return <Navigate to="/blog" replace />;
  }

  const Icon = category.icon;
  const categoryPosts = blogPosts.filter(post => 
    post.category.toLowerCase().replace(/\s+/g, '-') === category.slug ||
    post.category === category.name ||
    post.capsulaTheme === category.id.replace(/-/g, '-')
  );
  
  const categoryCapsules = capsulas.filter(capsula =>
    capsula.category.toLowerCase().replace(/\s+/g, '-') === category.slug
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/blog">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Volver al blog
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-background via-muted/20 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${category.color} text-white`}>
              <Icon className="w-12 h-12" />
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-foreground">
              {category.name}
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
              {category.description}
            </p>

            <div className="flex flex-wrap justify-center gap-2 pt-4">
              {category.targetAudience.map((audience) => (
                <Badge key={audience} variant="secondary" className="px-3 py-1">
                  {audience}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Description & Benefits */}
      <section className="py-16 border-t">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-2xl">¿Qué incluye esta categoría?</CardTitle>
                <CardDescription className="text-lg">
                  {category.longDescription}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <h3 className="font-semibold text-lg mb-4">Beneficios principales:</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {category.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Cápsulas */}
      {categoryCapsules.length > 0 && (
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Cápsulas de Implementación
                </h2>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                  Guías prácticas paso a paso para implementar estas soluciones en tu negocio
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryCapsules.map((capsula) => {
                  const relatedPosts = blogPosts.filter(post =>
                    capsula.relatedPostIds.includes(post.id)
                  );
                  
                  return (
                    <CapsuleCard 
                      key={capsula.id}
                      icon={capsula.icon}
                      title={capsula.title}
                      description={capsula.description}
                      difficulty={capsula.difficulty}
                      timeToImplement={capsula.timeToImplement}
                      relatedPosts={relatedPosts}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Artículos */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Artículos y Casos de Uso
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                {categoryPosts.length} {categoryPosts.length === 1 ? 'artículo' : 'artículos'} con ejemplos reales y resultados comprobados
              </p>
            </div>
            
            {categoryPosts.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {categoryPosts.map((post) => (
                  <BlogCard key={post.id} post={post} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No hay artículos disponibles en esta categoría todavía.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-primary/5 to-primary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold">
              ¿Listo para implementar estas soluciones?
            </h2>
            <p className="text-lg text-muted-foreground">
              En Globaltis te ayudamos a integrar estas herramientas en tu día a día. 
              Sin compromiso, sin complicaciones. Solo IA que funciona.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Link to="/contacto">
                <Button size="lg" className="w-full sm:w-auto">
                  Hablar con un experto
                </Button>
              </Link>
              <Link to="/capsulas">
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  Ver todas las cápsulas
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Category;
